import React, { useEffect, useState } from 'react';
import axios from 'axios';

// Grows one entry at a time as we discover another screen that quietly
// depends on tenant-level setup (e.g. the Inc-GST radio on UoM Reference
// Prices silently disabling itself with no default Tax Code). Not meant to
// be an exhaustive audit up front — just append a check whenever a tenant
// hits a wall like that. Tabbed by the module the check's *dependent*
// screen lives in (not where the prerequisite itself is configured) — this
// stays one backend admin tool rather than scattering setup nudges across
// every module's own UI. Only lists the tenant must supply itself — anything
// in the starter set (backend/config/tenantDefaults.js) is seeded, not
// checked here.
const MODULES = [
  { key: 'platform', label: 'Platform' },
  { key: 'inventory', label: 'Inventory' },
  { key: 'sales', label: 'Sales' },
  { key: 'accounting', label: 'Accounting' },
];

const CHECKS = [
  {
    module: 'platform',
    id: 'currency-codes',
    label: 'Currency Codes',
    description: 'Needed before Company Information can set the tenant\'s trading currency.',
    fixPageKey: 'accounting-currency',
    fixLabel: 'Add Currency Codes',
    run: async () => {
      try {
        const res = await axios.get('/api/currencies', { params: { status: 'active' } });
        return (res.data.pagination?.total ?? 0) > 0;
      } catch {
        return false;
      }
    },
  },
  {
    module: 'platform',
    id: 'company-info',
    label: 'Company Information',
    description: 'Company name and details, used across quotes, reports and the app header.',
    fixPageKey: 'company-info',
    fixLabel: 'Set up Company Information',
    run: async () => {
      try {
        await axios.get('/api/company-settings');
        return true;
      } catch {
        return false;
      }
    },
  },
  {
    module: 'platform',
    id: 'suburbs',
    label: 'Suburbs & Postcodes',
    description: 'Needed to enter addresses anywhere in the app (Company Information, Customers, etc).',
    fixPageKey: 'suburbs',
    fixLabel: 'Add Suburbs & Postcodes',
    run: async () => {
      try {
        const res = await axios.get('/api/suburbs', { params: { status: 'active' } });
        return (res.data.pagination?.total ?? 0) > 0;
      } catch {
        return false;
      }
    },
  },
  {
    module: 'inventory',
    id: 'product-categories',
    label: 'Product Categories',
    description: 'A tenant always has the auto-created "Uncategorised" default — this checks for at least one real category beyond that.',
    fixPageKey: 'inventory-product-categories',
    fixLabel: 'Add Product Categories',
    run: async () => {
      try {
        const res = await axios.get('/api/product-categories');
        return (res.data.data || []).some((c) => !c.is_system_default);
      } catch {
        return false;
      }
    },
  },
  {
    module: 'inventory',
    id: 'uom',
    label: 'Units of Measure',
    description: 'Needed before any Product can be given a sellable Unit of Measure or price.',
    fixPageKey: 'inventory-uom',
    fixLabel: 'Add Units of Measure',
    run: async () => {
      try {
        const res = await axios.get('/api/uoms', { params: { status: 'active' } });
        return (res.data.data || []).length > 0;
      } catch {
        return false;
      }
    },
  },
  {
    module: 'inventory',
    id: 'suppliers',
    label: 'Suppliers',
    description: 'Needed before a Supplier-linked Product\'s Cost Price can be entered — Suppliers itself lives under Purchasing.',
    fixPageKey: 'suppliers',
    fixLabel: 'Add Suppliers',
    run: async () => {
      try {
        const res = await axios.get('/api/suppliers', { params: { status: 'active' } });
        return (res.data.data || []).length > 0;
      } catch {
        return false;
      }
    },
  },
  {
    module: 'inventory',
    id: 'supplier-currency',
    label: 'Supplier Currency',
    description: 'Every active Supplier needs a Currency, so its costs are recorded in it and converted at that currency’s reference exchange rate.',
    fixPageKey: 'suppliers',
    fixLabel: 'Review Suppliers',
    // A missing currency_code is reachable by giving an existing record the
    // Supplier Relationship Type from its Relationship card, which doesn't
    // ask for one — fixed from the record's own Currency card. A currency
    // with no reference rate today is "rate missing", not a setup gap
    // (issue #303), so exchange_rate isn't checked here.
    run: async () => {
      try {
        const res = await axios.get('/api/suppliers', { params: { status: 'active' } });
        return (res.data.data || []).every((s) => !!s.currency_code);
      } catch {
        return false;
      }
    },
  },
  {
    module: 'inventory',
    id: 'cost-entry-exchange-rates',
    label: 'Cost Exchange Rates',
    description: 'Every foreign-currency Cost Price entry needs an exchange rate. One saved while the reference rate couldn’t be retrieved shows "rate missing" until a rate is entered or retrieved.',
    fixPageKey: 'products',
    fixLabel: 'Review Products',
    run: async () => {
      try {
        const res = await axios.get('/api/products/cost-prices-missing-rate');
        return Number(res.data.data?.count) === 0;
      } catch {
        return false;
      }
    },
  },
  {
    module: 'inventory',
    id: 'product-cost-price-schedule',
    label: 'Cost Price Schedule',
    description: 'Every active Product needs a current entry on its dated Cost Price schedule — Supplier-linked Products from the Supplier\'s Cost Price Schedule page, others from the Product\'s own Cost Price screen.',
    fixPageKey: 'products',
    fixLabel: 'Review Products',
    // cost_price/cost_price_foreign are resolved by the backend
    // (productController.js's withAvailability) from each Product's
    // cost_schedule — both null here means that Product's schedule has no
    // entry covering today (including a Product that's never had one added
    // yet at all).
    run: async () => {
      try {
        const res = await axios.get('/api/products', { params: { status: 'active', all: true } });
        const rows = res.data.data || [];
        return rows.length > 0 && rows.every((p) => (p.cost_price !== null && p.cost_price !== undefined) || (p.cost_price_foreign !== null && p.cost_price_foreign !== undefined));
      } catch {
        return false;
      }
    },
  },
  {
    module: 'accounting',
    id: 'quickbooks',
    label: 'Connect QuickBooks',
    description: 'Needed before anything can be read from or sent to QuickBooks Online. Only an Admin can connect it.',
    fixPageKey: 'global-api-integrations',
    fixLabel: 'Connect QuickBooks',
    run: async () => {
      try {
        const res = await axios.get('/api/quickbooks');
        return (res.data.data?.status || 'not_connected') !== 'not_connected';
      } catch {
        return false;
      }
    },
  },
  {
    module: 'accounting',
    id: 'quickbooks-customer-fields',
    label: 'Map QuickBooks Customer fields',
    description: 'Which Address Type fills the QuickBooks Billing and Shipping Address, and which Email Type (if any) its Billing Email. Customers can’t be linked to QuickBooks until both addresses are mapped.',
    fixPageKey: 'accounting-quickbooks',
    fixLabel: 'Map Customer fields',
    run: async () => {
      try {
        const res = await axios.get('/api/quickbooks/customer-field-mapping');
        return !!res.data.data?.complete;
      } catch {
        return false;
      }
    },
  },
  {
    module: 'accounting',
    id: 'quickbooks-setup-mappings',
    label: 'Match QuickBooks tax codes, terms and shipping',
    description: 'Each active Tax Code and Payment Term matched to its QuickBooks equivalent, and the QuickBooks Item shipping charges are invoiced against, so Invoices carry the right GST, terms and freight line.',
    fixPageKey: 'accounting-quickbooks',
    fixLabel: 'Match in QuickBooks',
    run: async () => {
      try {
        const res = await axios.get('/api/quickbooks/mappings/status');
        return !!res.data.data?.complete;
      } catch {
        return false;
      }
    },
  },
];

function SetupChecklistPage({ onNavigate }) {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(MODULES[0].key);

  useEffect(() => {
    let cancelled = false;
    const runnable = CHECKS.filter((c) => c.run);
    Promise.all(runnable.map((c) => c.run())).then((outcomes) => {
      if (cancelled) return;
      const byId = {};
      runnable.forEach((c, i) => { byId[c.id] = outcomes[i]; });
      setResults(byId);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  const tabChecks = CHECKS.filter((c) => c.module === activeTab);
  const countableChecks = tabChecks.filter((c) => c.run);
  const doneCount = results ? countableChecks.filter((c) => results[c.id]).length : 0;

  return (
    <div style={{ margin: '32px 24px 48px', background: '#fafafa', border: '1px solid #eceef1', borderRadius: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', padding: 24 }}>
      <h2 style={{ margin: '0 0 4px', fontWeight: 600, fontSize: 20 }}>Setup Checklist</h2>
      <p style={{ color: '#666', fontSize: 14, marginTop: 0 }}>
        Tenant-wide configuration that other screens quietly depend on. Grown one check at a
        time as they're discovered — not a guarantee that nothing else is missing.
      </p>

      <div style={{ display: 'flex', gap: 4, marginTop: 16, borderBottom: '1px solid #e3e8ef' }}>
        {MODULES.map((m) => (
          <button
            key={m.key}
            type="button"
            onClick={() => setActiveTab(m.key)}
            style={{
              padding: '8px 16px',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === m.key ? '2px solid #1976d2' : '2px solid transparent',
              color: activeTab === m.key ? '#1976d2' : '#5a6b85',
              fontWeight: activeTab === m.key ? 600 : 500,
              fontSize: 14,
              cursor: 'pointer',
              marginBottom: -1,
            }}
          >
            {m.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ padding: '20px 0' }}>Loading...</div>
      ) : (
        <>
          <div style={{ fontSize: 13, color: '#888', margin: '16px 0' }}>{doneCount} of {countableChecks.length} complete</div>
          {tabChecks.length === 0 ? (
            <div style={{ padding: '14px 16px', background: '#fff', borderRadius: 8, border: '1px solid #e3e8ef', color: '#888', fontSize: 14 }}>
              No checks yet for this module.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {tabChecks.map((c, i) => {
                const done = c.run ? results[c.id] : null;
                return (
                  <div
                    key={c.id}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px 16px', background: '#fff', borderRadius: 8, border: '1px solid #e3e8ef' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      <span style={{ minWidth: 16, textAlign: 'right', fontSize: 13, color: '#888' }}>{i + 1}.</span>
                      <span style={{ fontSize: 16, lineHeight: '20px', color: done ? '#2e7d32' : '#bfc8d6' }}>{c.comingSoon ? '—' : (done ? '✓' : '○')}</span>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>{c.label}</div>
                        <div style={{ fontSize: 12, color: '#888' }}>{c.description}</div>
                      </div>
                    </div>
                    {c.comingSoon ? (
                      <span style={{ whiteSpace: 'nowrap', fontSize: 13, color: '#888', fontStyle: 'italic' }}>Coming soon</span>
                    ) : onNavigate && (
                      <button
                        type="button"
                        onClick={() => onNavigate(c.fixPageKey)}
                        style={done
                          ? { whiteSpace: 'nowrap', padding: '6px 14px', background: 'none', color: '#1976d2', border: '1px solid #bfc8d6', borderRadius: 6, fontWeight: 500, cursor: 'pointer', fontSize: 13 }
                          : { whiteSpace: 'nowrap', padding: '6px 14px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 500, cursor: 'pointer', fontSize: 13 }}
                      >
                        {done ? 'Open' : c.fixLabel}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default SetupChecklistPage;
